import { useQuery } from "@tanstack/react-query";
import { BACKEND, api, errMsg, fmtDate, money } from "@/lib/api";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrintButton } from "@/components/PrintButton";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border bg-stone-50 p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</p>
    <p className="mt-1 font-mono-num text-xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

const minus = (v) => (v > 0 ? `−${money(v)}` : money(0));
const signed = (v) => (v < 0 ? `−${money(-v)}` : money(v));
const Num = ({ children, className = "", testid }) => <td className={`whitespace-nowrap py-3 pr-4 font-mono-num ${className}`} data-testid={testid}>{children}</td>;

const Row = ({ r, heldCol }) => (
  <tr className="border-t" data-testid={`payout-row-${r.booking_id}`}>
    <td className="py-3 pr-4"><p className="font-medium">{r.listing_title}</p><p className="text-xs text-stone-500">{fmtDate(r.check_in)} → {fmtDate(r.check_out)}{r.status === "cancelled" && " · cancelled"}</p></td>
    <Num>{money(r.rent)}</Num>
    <Num>{money(r.tax_to_host)}</Num>
    <Num className="text-sky-700">{r.refunded > 0 ? money(r.refunded) : "—"}</Num>
    <Num>{minus(r.fairstay_fee)}</Num>
    {heldCol && <Num className="text-amber-700">{minus(r.tax_held_by_fairstay)}</Num>}
    <Num>{minus(r.stripe_fee)}</Num>
    <Num className="font-semibold" testid={`payout-amount-${r.booking_id}`}>{signed(r.payout)}</Num>
    <td className="whitespace-nowrap py-3 text-stone-600">{r.paid_out_on ? fmtDate(r.paid_out_on) : "—"}</td>
  </tr>
);

const sum = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
const TotalsRow = ({ rows, heldCol, p = "" }) => (
  <tr className="border-t-2 font-semibold" data-testid={`${p}payout-totals-row`}>
    <td className="py-3 pr-4">Total</td>
    <Num testid={`${p}payout-total-rent`}>{money(sum(rows, "rent"))}</Num>
    <Num testid={`${p}payout-total-tax`}>{money(sum(rows, "tax_to_host"))}</Num>
    <Num className="text-sky-700">{money(sum(rows, "refunded"))}</Num>
    <Num testid={`${p}payout-total-commission`}>{minus(sum(rows, "fairstay_fee"))}</Num>
    {heldCol && <Num className="text-amber-700">{minus(sum(rows, "tax_held_by_fairstay"))}</Num>}
    <Num>{minus(sum(rows, "stripe_fee"))}</Num>
    <Num testid={`${p}payout-total-payout`}>{signed(sum(rows, "payout"))}</Num>
    <td />
  </tr>
);

const scheduleLabel = (d) => (d.schedule === "weekly" ? `weekly on ${d.weekly_anchor}s` : d.schedule || "manual");

const PayoutTable = ({ rows, testid }) => {
  const held = rows.some((r) => r.tax_held_by_fairstay > 0);
  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-sm" data-testid={testid}>
        <thead className="text-xs uppercase tracking-wider text-stone-500">
          <tr><th className="pb-2">Stay</th><th className="pb-2">Rental earnings (after refunds)</th><th className="pb-2">Lodging tax to you</th><th className="pb-2">Refunded to guest</th><th className="pb-2">FairStay 10%</th>{held && <th className="pb-2">Tax FairStay remits</th>}<th className="pb-2">Stripe fee</th><th className="pb-2">Your payout</th><th className="pb-2">Expected payout</th></tr>
        </thead>
        <tbody>{rows.map((r) => <Row key={r.booking_id} r={r} heldCol={held} />)}<TotalsRow rows={rows} heldCol={held} p={testid === "payout-test-table" ? "test-" : ""} /></tbody>
      </table>
    </div>
  );
};

function TestSection({ t, testMode }) {
  if (!t || (!t.bookings.length && !t.stripe)) return null;
  return (
    <div className="mt-8 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-5" data-testid="payout-test-section">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white" data-testid="payout-test-badge">Test</span>
        <p className="text-sm font-semibold text-amber-900">Test data: not real money and not counted in the totals above</p>
      </div>
      <p className="mt-1 text-xs text-amber-900">{testMode ? "This screen uses Stripe test mode, so no real money moves here." : "These are FairStay test stays, kept apart from your real earnings."}</p>
      {t.stripe && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Stat label="Test Stripe balance: pending" value={money(t.stripe.pending)} testid="payout-test-stripe-pending" />
          <Stat label="Test Stripe balance: available" value={money(t.stripe.available)} testid="payout-test-stripe-available" />
        </div>
      )}
      {t.bookings.length > 0 && <PayoutTable rows={t.bookings} testid="payout-test-table" />}
    </div>
  );
}

function LiveFigures({ data }) {
  const live = data.mode === "live";
  const differs = live && Math.abs(data.records.pending - data.stripe.pending) >= 0.01;
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Pending payout (FairStay paid stays)" value={money(live ? data.records.pending : 0)} testid="payout-records-pending" />
        <Stat label="Stripe live balance: pending" value={live ? money(data.stripe.pending) : "No live data"} testid="payout-stripe-pending" />
        <Stat label="Stripe live balance: available" value={live ? money(data.stripe.available) : "No live data"} testid="payout-stripe-available" />
        <Stat label="Next payout day" value={!live ? "—" : data.next_payout_date ? fmtDate(data.next_payout_date) : "Manual"} testid="payout-next-date" />
      </div>
      <p className="mt-3 text-xs text-stone-500" data-testid="payout-source-note">
        {live
          ? <>The first figure comes from your {data.records.stays} paid live FairStay stay{data.records.stays === 1 ? "" : "s"}. The Stripe figures come straight from your live Stripe account. Stripe pays you {scheduleLabel(data)}.</>
          : "Live figures appear here once real payments are switched on. Nothing on this screen is real money."}
        {differs && <span className="block pt-1 text-amber-800" data-testid="payout-differs-note">The two pending figures differ. Your Stripe balance can include money from outside FairStay or payments Stripe hasn't settled yet. Stripe's balance is what will actually be paid out.</span>}
      </p>
    </>
  );
}

// Payout statement: verified live figures (FairStay records + live Stripe balance), test data kept apart.
export function PayoutSummary() {
  const { data, error, isLoading } = useQuery({ queryKey: ["host-payouts"], queryFn: () => api.get("/host/payouts").then((r) => r.data), retry: false });
  if (isLoading || (data && !data.connected)) return null;
  const live = data?.mode === "live";
  return (
    <section id="payout-statement" className="mb-10 rounded-3xl border bg-white p-6" data-testid="payout-summary">
      <div className="mb-5 flex flex-wrap items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /><h2 className="text-lg font-semibold">Payout statement</h2>
        {data && <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${live ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`} data-testid="payout-mode-badge">{live ? "Live" : "Test mode"}</span>}
        {data?.bookings?.length > 0 && <span className="ml-auto print:hidden"><PrintButton targetId="payout-statement" testid="payout-statement-print" /></span>}
        {data?.bookings?.length > 0 && <Button asChild size="sm" variant="outline" className="rounded-full print:hidden" data-testid="payout-statement-download"><a href={`${BACKEND}/api/host/payouts?format=csv`}>Download spreadsheet</a></Button>}</div>
      {error && <p className="text-sm text-red-700" data-testid="payout-summary-error">{errMsg(error)}</p>}
      {data && (
        <>
          <LiveFigures data={data} />
          {data.bookings.length > 0 && (
            <>
              <PayoutTable rows={data.bookings} testid="payout-table" />
              <p className="mt-3 text-xs text-stone-500" data-testid="payout-tax-note">Lodging tax passed to you is yours to file and pay to the tax office unless FairStay's records show it remits that tax (pending CPA verification). FairStay's 10% is on rent and cleaning only, never on tax. Stripe's card fee isn't returned on refunds.</p>
            </>
          )}
          <TestSection t={data.test} testMode={!live} />
        </>
      )}
    </section>
  );
}
